import Box from '@mui/material/Box';
import { includes, pathOr } from 'ramda';
import React, { useCallback, useEffect, useState } from 'react';

import useAccounts from 'src/hooks/useAccountsHook';
import { getMoxoToken } from 'src/services/moxo';
import { USER_ONBOARD_APPLICANT } from 'src/services/constants/accounts';
import SiteSettings from 'src/siteSetting';

import { MoxoChatTypeEnum } from './Client/types';
import { MOXO_CONTAINER_ID } from './constants';
import ConversationPaneContainer from './ConversationPaneContainer';
import MoxoFab from './MoxoFab';
import { useMoxo } from './useMoxo';
import { reportException } from 'src/exceptionReporting';
import { isNotEmpty } from 'src/utilities/hasValue';


export interface MoxoLauncherProps {
  isDisabled?: boolean;
  token?: string;
}

export const MoxoLauncher = (props: MoxoLauncherProps) => {
  const { isDisabled = false, token } = props;

  const [isChatVisible, setIsChatVisible] = useState(false);
  const [isLoadingScript, setIsLoadingScript] = useState(false);

  const handleMoxoSubmitSuccess = useCallback(() => {
    setIsLoadingScript(false);
  }, []);

  const handleMoxoSubmitError = useCallback((err) => {
    reportException('MoxoLauncherMoxo', err)

    setIsLoadingScript(false);
  }, []);

  const { onClick, onLoad } = useMoxo({
    config: {
      channelId: SiteSettings.generalMoxoChannel(),
      clientType: MoxoChatTypeEnum.AUTHENTICATED,
      containerId: MOXO_CONTAINER_ID(),
      domain: SiteSettings.generalMoxoDomain(),
      token,
    },
    isDisabled,
    isLoading: isLoadingScript,
    onMoxoSubmitError: handleMoxoSubmitError,
    onMoxoSubmitSuccess: handleMoxoSubmitSuccess,
  });

  const handleClick = () => {
    if (isDisabled || isLoadingScript) {
      return;
    }

    if (!isChatVisible && onClick) {
      setIsChatVisible(true);
      setIsLoadingScript(true);
      onClick();
    }
  };


  const handleChatClose = () => {
    setIsChatVisible(false);
  };

  return (
    <Box sx={{ bottom: '200px', position: 'fixed', right: '40px', zIndex:999 }}>
      {isChatVisible && (
        <ConversationPaneContainer
          isChatVisible={isChatVisible}
          isLoading={isLoadingScript}
          onChatLoad={onLoad}
          onCloseChat={handleChatClose}
          onMoxoSubmitError={handleMoxoSubmitError}
          onMoxoSubmitSuccess={handleMoxoSubmitSuccess}
          onToggleMinimize={handleChatClose}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <MoxoFab
          isChatVisible={isChatVisible}
          isDisabled={isDisabled || isLoadingScript}
          onOpenChatClick={handleClick}
          onToggleMinimize={handleChatClose}
        />
      </Box>
    </Box>
  );
};

const MoxoAuthenticatedLauncher = ({
  isDisabled,
}: {
  isDisabled?: boolean;
}) => {
  const { accountEmail, accountFirstName, accountId, accountLastName, accountRoles = [] } =
    useAccounts();
  const [token, setToken] = useState<null | string>(null);
  const enableMoxo = includes(USER_ONBOARD_APPLICANT, accountRoles) && isNotEmpty(SiteSettings.generalMoxoDomain())

  async function getToken() {
    const payload = {
      email: accountEmail,
      first_name: accountFirstName,
      last_name: accountLastName,
      unique_id: accountId,
    };

    const response = await getMoxoToken(payload);
    const token = pathOr('', ['access_token'], response);
    setToken(token);
  }

  useEffect(() => {
    if (enableMoxo) {
      getToken();
    }
  }, []);

  if (token) {
    return <MoxoLauncher isDisabled={isDisabled} token={token} />;
  }
  return null;
};

export default MoxoAuthenticatedLauncher;